import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { SectionHeader, EmptyState, Spinner, DataTable } from '../../components/ui';
import { brokerApi } from '../../api/brokerClient';
import { ROUTES } from '../../routes/constants';
import { fmtValue, normalizeHoldingRow } from './bookUtils';

export function HoldingsBody({ onRefreshChange } = {}) {
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['brokerHoldingsPage'],
    queryFn: brokerApi.getHoldings,
    staleTime: 10_000,
    refetchInterval: 15_000,
    retry: 1,
  });

  const rows = Array.isArray(data?.holdings) ? data.holdings.map(normalizeHoldingRow) : [];
  const handleRefresh = useCallback(() => refetch(), [refetch]);

  useEffect(() => {
    onRefreshChange?.(handleRefresh);
    return () => onRefreshChange?.(null);
  }, [onRefreshChange, handleRefresh]);

  const columns = [
    { key: 'symbol',        label: 'Symbol',        render: (v) => fmtValue(v) },
    { key: 'exchange',      label: 'Exchange',      render: (v) => fmtValue(v), mobileHidden: true },
    { key: 'qty',           label: 'Qty',           render: (v) => fmtValue(v) },
    { key: 'collateralQty', label: 'Collateral Qty',render: (v) => fmtValue(v), mobileHidden: true },
    { key: 'avgPrice',      label: 'Avg Price',     render: (v) => fmtValue(v), mobileHidden: true },
    { key: 'ltp',           label: 'LTP',           render: (v) => fmtValue(v) },
    { key: 'pnl',           label: 'PnL / Change',  render: (v) => fmtValue(v) },
  ];

  return (
    <>
      <div className="panel p-4">
        {isLoading ? (
          <div className="flex justify-center py-12"><Spinner size={18} /></div>
        ) : error ? (
          <EmptyState title="Could not load holdings" description={error.message} />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No holdings yet"
            description="Long-term holdings from your connected broker will appear here."
            action={(
              <button onClick={() => navigate(ROUTES.BROKER)} className="btn-secondary mt-4 text-xs">
                Manage broker connection
              </button>
            )}
          />
        ) : (
          <DataTable columns={columns} data={rows} />
        )}
      </div>
    </>
  );
}

export default function BrokerHoldingsPage() {
  return (
    <div className="max-w-7xl space-y-5">
      <SectionHeader title="Holdings" subtitle="Connected broker holdings." />
      <HoldingsBody />
    </div>
  );
}
